// Building the engine's view of a tune: the written changes, their analysis,
// the authored chord each sequence entry came from, and the melody in
// absolute beats so any sub-span (a ii–V's two halves) can ask what sounds
// over it. Written order deliberately (flatMeasuresToSequence over the
// measures, not the played form) — a reharm edits the written bar.

import { analyzeHarmony } from '../analysis';
import { flatMeasuresToSequence } from '../chart';
import { TICKS_PER_BEAT } from '../chartMelody';
import { pitchClassOf } from '../notes';
import { scoreToChart } from '../score/convert';
import type { Score } from '../score/types';
import type { MelodyNoteSlice, ReharmContext } from './types';

export function buildReharmContext(score: Score): ReharmContext {
  const chart = scoreToChart(score);
  const sequence = flatMeasuresToSequence(chart.measures, 'middle', chart.timeSignature);
  const analysis = analyzeHarmony(sequence, chart.key);

  // Absolute bar starts in beats (denominator units), pickup-aware like the
  // sequence builder itself.
  const barStart: number[] = [];
  const barMeterBeats: number[] = [];
  let meter = chart.timeSignature;
  let cursor = 0;
  for (const measure of chart.measures) {
    meter = measure.timeSig ?? meter;
    barStart.push(cursor);
    barMeterBeats.push(meter[0]);
    cursor += measure.pickupBeats ?? meter[0];
  }

  // flatMeasuresToSequence mints `${measureId}-${barIndex}-${ci}`; ci indexes
  // the measure's chord array INCLUDING N.C. entries, so it is the harmony
  // index too (firstPassVoicingsByChordId parses the same way).
  const chordIds: string[] = [];
  const measureIds: string[] = [];
  const chordBeats: number[] = [];
  const chordStarts: number[] = [];
  const barBeats: number[] = [];
  for (const entry of sequence) {
    const parts = entry.id.split('-');
    const ci = Number(parts[parts.length - 1]);
    const barIndex = entry.barIndex;
    const measure = chart.measures[barIndex];
    const chord = measure?.chords[ci];
    chordIds.push(chord?.id ?? '');
    measureIds.push(measure?.id ?? '');
    chordBeats.push(chord?.beats ?? entry.durationBeats);
    chordStarts.push((barStart[barIndex] ?? 0) + (entry.beat - 1));
    barBeats.push(barMeterBeats[barIndex] ?? chart.timeSignature[0]);
  }

  let melody: MelodyNoteSlice[] | null = null;
  chart.measures.forEach((measure, barIndex) => {
    for (const note of measure.melody ?? []) {
      if (note.rest) continue;
      melody ??= [];
      const start = barStart[barIndex] + note.startTicks / TICKS_PER_BEAT;
      melody.push({
        startBeat: start,
        endBeat: start + note.durationTicks / TICKS_PER_BEAT,
        midi: note.midi,
        pc: ((note.midi % 12) + 12) % 12,
      });
    }
  });
  if (melody) {
    const line = melody as MelodyNoteSlice[];
    line.sort((a, b) => a.startBeat - b.startBeat);
    // Where the line goes next: a repeated pitch is not a departure, so the
    // "next" note is the first one that actually moves.
    line.forEach((note, i) => {
      const next = line.slice(i + 1).find((n) => n.midi !== note.midi);
      if (next) note.nextMidi = next.midi;
    });
  }

  return {
    score,
    sequence,
    analysis,
    globalKey: chart.key,
    chordIds,
    measureIds,
    chordBeats,
    chordStarts,
    barBeats,
    melody,
  };
}

/** Where the chord at `at` stops SOUNDING, in absolute beats. A chord rings
 *  on through bars that carry no symbol (flatMeasuresToSequence stretches its
 *  duration to the next written mark), so this can lie past its written
 *  beats: whatever a candidate's last chord is, it sounds under the melody
 *  sung all the way to here. */
export function heldEnd(ctx: ReharmContext, at: number): number {
  return ctx.chordStarts[at] + Math.max(ctx.chordBeats[at], ctx.sequence[at].durationBeats);
}

/** Sequence index of the authored chord `chordId`, or -1. */
export function sequenceIndexOf(ctx: ReharmContext, chordId: string): number {
  return ctx.chordIds.indexOf(chordId);
}

export interface SungPitchClass {
  pc: number;
  /** Beats this pitch class sounds for inside the span. */
  beats: number;
  /** Sounding at the span's onset. */
  onOnset: boolean;
  /** Some note of this pitch class in the span is left by a step or leap UP. */
  risesAfter: boolean;
}

/** Melody pitch classes sounding in [startBeat, endBeat), weighted by the
 *  beats each sounds for. Empty when the tune has no melody. */
export function melodyIn(ctx: ReharmContext, startBeat: number, endBeat: number): SungPitchClass[] {
  if (!ctx.melody) return [];
  const byPc = new Map<number, Omit<SungPitchClass, 'pc'>>();
  for (const note of ctx.melody) {
    const overlap = Math.min(note.endBeat, endBeat) - Math.max(note.startBeat, startBeat);
    if (overlap <= 1e-9) continue;
    const entry = byPc.get(note.pc) ?? { beats: 0, onOnset: false, risesAfter: false };
    entry.beats += overlap;
    if (note.startBeat <= startBeat + 1e-9 && note.endBeat > startBeat + 1e-9) entry.onOnset = true;
    if (note.nextMidi !== undefined && note.nextMidi > note.midi) entry.risesAfter = true;
    byPc.set(note.pc, entry);
  }
  return [...byPc.entries()].map(([pc, e]) => ({ pc, ...e }));
}

export const pc = (name: string): number => pitchClassOf(name);
